import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useASIItemMaster from '../../../../hooks/useASIItemMaster';
import { showLoading } from 'react-global-loading';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { API_URL } from '../../../../Constant';
import messages from '../../../../assets/Messages.json';
import HeaderInfo from '../../../../Components/Common/HeaderInfo';
import Remarks from './Remarks';
import Remarks__list from './Remarks__list';
import { markPageAsSaved, PAGE_NAMES, refreshBalanceSheetValidation } from '../../../../Components/BalanceSheet/BalanceSheetHeader';
import { filterRemarksList, handleDeleteTableRow } from '../../../../Custom/Custom';

function BalanceSheetData() {
  const navigate = useNavigate();

  // Refs to prevent multiple API calls
  const dataLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const apiCallMadeRef = useRef(false);

  // Use the custom hook for Balance Sheet Block Item data
  const {
    blockItemData,
    filteredBlockItems,
    isLoadingBlockItems,
    debouncedBlockItemSearch,
    loadMoreBlockItemData,
    hasMoreBlockItems,
    isLoadingMoreBlockItems,
  } = useASIItemMaster();

  // State for header data
  const [headerData, setHeaderData] = useState({
    despatchSlNo: '',
    permanentSlNo: '',
    companyName: '',
    dslNo: '',
    surveyYear: ''
  });

  // State for form data
  const [formData, setFormData] = useState({
    items: Array(50).fill(''), // 50 rows for items
    currentYear: Array(50).fill(''), // Current Year values
    previousYear: Array(50).fill(''), // Previous Year values
    status: Array(50).fill('') // Status for each row
  });

  // State for Remarks section
  const [remarksData, setRemarksData] = useState({
    remarksBlock: 'ALL', // Items dropdown (block)
    remarksItems: '', // Columns dropdown (Columns)
    remarks: '' // Remarks textarea (remarks)
  });

  // State for Remarks List
  const [remarksList, setRemarksList] = useState([]);

  // State to track how many rows are visible
  const [visibleRows, setVisibleRows] = useState(1);

  // Maximum rows limit
  const MAX_ROWS = 50;

  // State for dropdown management
  const [dropdownStates, setDropdownStates] = useState({});
  const [searchTerms, setSearchTerms] = useState({});
  const [selectedIndex, setSelectedIndex] = useState({}); // Track selected index for keyboard navigation
  const dropdownItemRefs = useRef({}); // Refs for dropdown items
  const justOpenedRef = useRef({}); // Track if dropdown was just opened to prevent immediate toggle

  // Authentication configuration
  const getAuthConfig = () => {
    let token = localStorage.getItem('jwt'); // Use JWT token first

    // Try alternative token keys if 'jwt' is not found
    if (!token) {
      token = localStorage.getItem('token') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('accessToken') ||
        localStorage.getItem('userToken');
    }

    console.log('Raw token from localStorage:', token);

    if (!token) {
      console.error('No authentication token found');
      toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
      return {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      };
    }

    // Clean the token (remove any extra spaces or quotes)
    const cleanToken = token.replace(/['"]/g, '').trim();
    console.log('Cleaned token:', cleanToken);

    return {
      headers: {
        'Authorization': `Bearer ${cleanToken}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    };
  };

  // Reset refs on component mount to ensure fresh API call
  useEffect(() => {
    // Reset all refs when component mounts
    dataLoadedRef.current = false;
    isLoadingRef.current = false;
    apiCallMadeRef.current = false;
    console.log('🔄 BalanceSheetData Component mounted - Refs reset for fresh API call');
  }, []);

  // Fetch header data from localStorage when component mounts
  useEffect(() => {
    const fetchHeaderData = () => {
      try {
        // Get form data from localStorage (stored by balanceSheet page)
        const balanceSheetData = localStorage.getItem('balanceSheetFormData');

        if (balanceSheetData) {
          const parsedData = JSON.parse(balanceSheetData);

          setHeaderData({
            despatchSlNo: parsedData.dslNo || '',
            permanentSlNo: parsedData.pslNo || '',
            companyName: parsedData.nameUnit || '',
            dslNo: parsedData.dslNo || '',
            surveyYear: parsedData.year || ''
          });
        } else {
          // If no data in localStorage, set defaults
          console.log('No balanceSheet data found in localStorage');
          setHeaderData({
            despatchSlNo: 'DSL No. not set',
            permanentSlNo: 'PSL No. not set',
            companyName: 'Company name not set',
            dslNo: '',
            surveyYear: ''
          });
        }
      } catch (error) {
        console.error('Error fetching data from localStorage:', error);
        setHeaderData({
          despatchSlNo: 'Error loading data',
          permanentSlNo: 'Error loading data',
          companyName: 'Error loading data',
          dslNo: '',
          surveyYear: ''
        });
      }
    };

    fetchHeaderData();
  }, []);

  // Consolidated useEffect for loading existing data - prevents multiple API calls
  useEffect(() => {
    // Check if all conditions are met and API hasn't been called yet
    const shouldLoadData = !dataLoadedRef.current &&
      !isLoadingRef.current &&
      !apiCallMadeRef.current &&
      headerData.despatchSlNo &&
      headerData.despatchSlNo !== 'DSL No. not set' &&
      headerData.despatchSlNo !== 'Error loading data' &&
      headerData.surveyYear &&
      headerData.surveyYear.trim() !== '';

    // Debug logging
    console.log('=== BalanceSheetData API Call Check ===');
    console.log('dataLoadedRef.current:', dataLoadedRef.current);
    console.log('isLoadingRef.current:', isLoadingRef.current);
    console.log('apiCallMadeRef.current:', apiCallMadeRef.current);
    console.log('headerData.despatchSlNo:', headerData.despatchSlNo);
    console.log('headerData.surveyYear:', headerData.surveyYear);
    console.log('blockItemData.length:', blockItemData.length);
    console.log('shouldLoadData:', shouldLoadData);
    console.log('=====================================');

    if (shouldLoadData) {
      // Only load data if token exists
      const token = localStorage.getItem('jwt') || localStorage.getItem('token');
      if (token) {
        console.log('✅ Triggering loadExistingData - all conditions met');
        console.log('DSL No:', headerData.despatchSlNo);
        console.log('Survey Year:', headerData.surveyYear);
        // Mark as called immediately to prevent duplicate calls
        apiCallMadeRef.current = true;
        // Call loadExistingData directly - it will handle its own validation
        loadExistingData();
      } else {
        console.log('❌ No token found, skipping data load');
        // Reset ref if token not found so it can retry
        apiCallMadeRef.current = false;
      }
    } else {
      console.log('❌ Conditions not met for API call');
      if (dataLoadedRef.current) console.log('  - Data already loaded');
      if (isLoadingRef.current) console.log('  - Already loading');
      if (apiCallMadeRef.current) console.log('  - API call already made');
      if (!headerData.despatchSlNo || headerData.despatchSlNo === 'DSL No. not set' || headerData.despatchSlNo === 'Error loading data') console.log('  - DSL No. not available');
      if (!headerData.surveyYear || headerData.surveyYear.trim() === '') console.log('  - Survey Year not available');
    }
  }, [headerData.despatchSlNo, headerData.surveyYear]); // Depend on headerData changes

  // Additional useEffect to ensure API is called when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!dataLoadedRef.current && !isLoadingRef.current && !apiCallMadeRef.current &&
        blockItemData.length > 0) { // Wait for blockItemData to be loaded
        console.log('Fallback: Triggering loadExistingData after timeout - blockItemData loaded');
        const token = localStorage.getItem('jwt') || localStorage.getItem('token');
        if (token && headerData.despatchSlNo && headerData.despatchSlNo !== 'DSL No. not set') {
          loadExistingData();
        }
      }
    }, 2000); // Wait 2 seconds for blockItemData to be loaded

    return () => clearTimeout(timer);
  }, [blockItemData]); // Add blockItemData as dependency

  // Function to toggle dropdown for specific row
  const toggleDropdown = (rowIndex) => {
    setDropdownStates(prev => {
      const newState = {};
      // Close all other dropdowns and toggle current one
      const isCurrentlyOpen = prev[rowIndex] || false;
      newState[rowIndex] = !isCurrentlyOpen;
      return newState;
    });
    // Reset selected index when opening dropdown
    setSelectedIndex(prev => {
      const currentState = prev[rowIndex];
      if (currentState === undefined || currentState >= 0) {
        return { ...prev, [rowIndex]: -1 };
      }
      return prev;
    });
  };

  // Function to open dropdown (without toggle)
  const openDropdown = (rowIndex) => {
    setDropdownStates(prev => {
      // If already open, don't do anything
      if (prev[rowIndex]) {
        return prev;
      }
      const newState = {};
      // Close all other dropdowns and open current one
      newState[rowIndex] = true;
      // Mark as just opened to prevent immediate toggle (longer delay for touch/slow devices)
      justOpenedRef.current[rowIndex] = true;
      setTimeout(() => {
        justOpenedRef.current[rowIndex] = false;
      }, 300);
      return newState;
    });
    // Reset selected index when opening dropdown
    setSelectedIndex(prev => ({ ...prev, [rowIndex]: -1 }));
  };

  // Function to check if dropdown should open upward
  const shouldOpenUpward = (rowIndex) => {
    const inputElement = document.querySelector(`input[data-row="${rowIndex}"]`);
    if (!inputElement) return false;

    const rect = inputElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 200;

    // Check if there's enough space below
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    return spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;
  };

  const getDropdownPosition = (rowIndex) => {
    const inputElement = document.querySelector(`input[data-row="${rowIndex}"]`);
    if (!inputElement) return { top: '100%', bottom: 'auto' };

    const rect = inputElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 200;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      return { top: 'auto', bottom: '100%' };
    } else {
      return { top: '100%', bottom: 'auto' };
    }
  };

  // Function to get dropdown style
  const getDropdownStyle = (rowIndex) => {
    const position = getDropdownPosition(rowIndex);
    const isUpward = shouldOpenUpward(rowIndex);

    return {
      ...position,
      left: 0,
      right: 0,
      zIndex: 99999,
      maxHeight: '200px',
      overflowY: 'auto',
      overflowX: 'hidden',
      minWidth: '250px',
      width: 'max-content',
      maxWidth: '400px',
      border: '1px solid #ddd',
      boxShadow: isUpward ? '0 -4px 6px rgba(0, 0, 0, 0.1)' : '0 4px 6px rgba(0, 0, 0, 0.1)',
      position: 'absolute',
      marginTop: isUpward ? '0' : '2px',
      marginBottom: isUpward ? '2px' : '0',
      transform: 'translateY(0)',
      willChange: 'transform',
      scrollbarWidth: 'thin',
      scrollbarColor: '#ccc #f1f1f1',
      backgroundColor: 'white',
      borderRadius: '4px'
    };
  };

  // Function to close dropdown for specific row
  const closeDropdown = (rowIndex) => {
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState[rowIndex] = false;
      return newState;
    });
    // Reset selected index when closing dropdown
    setSelectedIndex(prev => ({ ...prev, [rowIndex]: -1 }));
  };

  // Function to close all dropdowns
  const closeAllDropdowns = () => {
    setDropdownStates({});
    setSelectedIndex({});
  };

  // Handle search input change
  const handleSearchChange = (rowIndex, value) => {
    setSearchTerms(prev => ({
      ...prev,
      [rowIndex]: value
    }));

    // Use the hook's debounced search
    debouncedBlockItemSearch(value);
  };

  // Handle click/touch outside to close dropdown (works on both mouse and touch devices)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.balance-sheet-item-dropdown')) {
        closeAllDropdowns();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Sample items list for dropdown (fallback data)
  const allItemsList = [
    { itemCode: '001', itemCodeName: 'Bills Receivable' },
    { itemCode: '002', itemCodeName: 'Cash in Hand' },
    { itemCode: '003', itemCodeName: 'Cash at Bank' },
    { itemCode: '004', itemCodeName: 'Loans & Advances' },
    { itemCode: '005', itemCodeName: 'Sundry Debtors' },
    { itemCode: '006', itemCodeName: 'Stock in Trade' },
    { itemCode: '007', itemCodeName: 'Fixed Assets' },
    { itemCode: '008', itemCodeName: 'Investments' },
    { itemCode: '009', itemCodeName: 'Sundry Creditors' },
    { itemCode: '010', itemCodeName: 'Bank Overdraft' },
    { itemCode: '011', itemCodeName: 'Outstanding Expenses' },
    { itemCode: '012', itemCodeName: 'Capital Account' },
    { itemCode: '013', itemCodeName: 'Reserves & Surplus' },
    { itemCode: '014', itemCodeName: 'Long Term Loans' },
    { itemCode: '015', itemCodeName: 'Current Liabilities' }
  ];

  // Handle dropdown change for items
  const handleItemChange = (index, selectedItem) => {
    const newItems = [...formData.items];
    const newStatus = [...formData.status];

    if (selectedItem) {
      newItems[index] = selectedItem.item_name || selectedItem.itemCodeName || '';
      newStatus[index] = selectedItem.block_item || ''; // Direct mapping since block_item is already formatted

      // Automatically add a new row if this is the last visible row
      if (index === visibleRows - 1 && visibleRows < MAX_ROWS) {
        setVisibleRows(visibleRows + 1);
      }
    } else {
      newItems[index] = '';
      newStatus[index] = '';
    }

    setFormData({
      ...formData,
      items: newItems,
      status: newStatus
    });

    // Close dropdown after selection
    closeDropdown(index);
  };

  // Handle keyboard navigation in dropdown
  const handleKeyDown = (e, rowIndex, filteredItemsList) => {
    const currentSelected = selectedIndex[rowIndex] !== undefined ? selectedIndex[rowIndex] : -1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        // Open dropdown if closed
        if (!dropdownStates[rowIndex]) {
          openDropdown(rowIndex);
        }
        // Move selection down
        if (currentSelected < filteredItemsList.length - 1) {
          const newIndex = currentSelected + 1;
          setSelectedIndex(prev => ({ ...prev, [rowIndex]: newIndex }));
          // Scroll into view
          setTimeout(() => {
            const itemRef = dropdownItemRefs.current[`${rowIndex}-${newIndex}`];
            if (itemRef) {
              itemRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        // Open dropdown if closed
        if (!dropdownStates[rowIndex]) {
          openDropdown(rowIndex);
        }
        // Move selection up
        if (currentSelected > 0) {
          const newIndex = currentSelected - 1;
          setSelectedIndex(prev => ({ ...prev, [rowIndex]: newIndex }));
          // Scroll into view
          setTimeout(() => {
            const itemRef = dropdownItemRefs.current[`${rowIndex}-${newIndex}`];
            if (itemRef) {
              itemRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
        }
        break;

      case 'Enter':
        e.preventDefault();
        if (dropdownStates[rowIndex] && currentSelected >= 0 && currentSelected < filteredItemsList.length) {
          // Select the highlighted item
          handleItemChange(rowIndex, filteredItemsList[currentSelected]);
        } else if (!dropdownStates[rowIndex]) {
          // Open dropdown if closed
          openDropdown(rowIndex);
        }
        break;

      case 'Escape':
        e.preventDefault();
        // Close dropdown
        closeDropdown(rowIndex);
        break;

      case 'Tab':
        // Close dropdown on tab
        closeDropdown(rowIndex);
        break;

      default:
        // For other keys, open dropdown if typing
        if (!dropdownStates[rowIndex] && e.key.length === 1) {
          openDropdown(rowIndex);
        }
        break;
    }
  };

  // Handle input changes for other fields with validation
  const handleInputChange = (index, field, value) => {
    const newFormData = { ...formData };

    // Apply numeric validation for currentYear and previousYear fields
    if (['currentYear', 'previousYear'].includes(field)) {
      let validatedValue = value;

      // Allow empty string, '0', or valid numeric input with optional decimal
      if (value === '' || value === '0' || /^\d*\.?\d*$/.test(value)) {
        // If decimal present, limit to 2 decimal places
        if (value.includes('.')) {
          const parts = value.split('.');
          if (parts[1] && parts[1].length > 2) {
            validatedValue = parts[0] + '.' + parts[1].substring(0, 2);
          }
        }

        // Limit total length to 15 characters
        if (validatedValue.length > 15) {
          validatedValue = validatedValue.substring(0, 15);
        }
      } else {
        // If invalid input, don't update the field
        return;
      }

      newFormData[field][index] = validatedValue;
    } else {
      newFormData[field][index] = value;
    }

    setFormData(newFormData);
  };

  // formatStatusFlag function removed - direct mapping used since block_item is already formatted

  // Function to reset form data
  const handleReset = () => {
    setFormData({
      items: Array(50).fill(''),
      currentYear: Array(50).fill(''),
      previousYear: Array(50).fill(''),
      status: Array(50).fill('')
    });

    setVisibleRows(1);
    setDropdownStates({});
    setSearchTerms({});

    // Clear API call references
    dataLoadedRef.current = false;
    isLoadingRef.current = false;
    apiCallMadeRef.current = false;

    toast.success('Form reset successfully!', messages.COLOR_RED);
  };

  // Load existing data from API
  const loadExistingData = async () => {
    // Validate required data before making API call
    if (!headerData.despatchSlNo || headerData.despatchSlNo === 'DSL No. not set' || headerData.despatchSlNo === 'Error loading data' || !headerData.surveyYear || headerData.surveyYear.trim() === '') {
      console.log('❌ loadExistingData: Missing required data - DSL No. or Survey Year');
      console.log('DSL No:', headerData.despatchSlNo);
      console.log('Survey Year:', headerData.surveyYear);
      // Reset ref if validation fails
      apiCallMadeRef.current = false;
      return;
    }

    // Prevent multiple calls - check refs first (double check)
    if (isLoadingRef.current) {
      console.log('loadExistingData: Already loading, skipping...');
      return;
    }

    // Mark as loading immediately to prevent duplicate calls
    isLoadingRef.current = true;

    // Show global loading
    showLoading(true);

    try {
      const payload = {
        dsl_no: headerData.despatchSlNo,
        survey_year: headerData.surveyYear.split('-')[0] || '2022' // Extract year from format like "2022-23"
      };

      console.log('Loading existing BlockD data with payload:', payload);
      const response = await axios.post(`${API_URL}api/blockd/get-data`, payload, getAuthConfig());
      console.log('BlockD GET API Response:', response.data);
      console.log('API Response Data Structure:', JSON.stringify(response.data, null, 2));

      if (response.data && response.data.status && response.data.data) {
        const existingData = response.data.data;

        // Initialize form data with existing values
        const newFormData = {
          items: Array(50).fill(''),
          currentYear: Array(50).fill(''),
          previousYear: Array(50).fill(''),
          status: Array(50).fill('')
        };

        // Populate with existing data - mapping API response to form fields
        existingData.forEach((item, index) => {
          if (index < MAX_ROWS) {
            console.log(`Processing item ${index}:`, item);
            console.log(`Available fields in item:`, Object.keys(item));
            console.log(`blockItem value:`, item.blockItem);
            console.log(`block_item value:`, item.block_item);
            console.log(`BLOCK_ITEM value:`, item.BLOCK_ITEM);

            // Find matching item in dropdown data using itemName
            const matchingItem = blockItemData.find(dropdownItem =>
              dropdownItem.item_name === item.itemName
            );

            // Get blockItem from API response with fallback
            let blockItemValue = item.blockItem || item.block_item || item.BLOCK_ITEM || item.blockItemCode || '';

            // If no blockItem from API, try to get from dropdown data
            if (!blockItemValue && matchingItem) {
              blockItemValue = matchingItem.block_item || '';
            }

            // If still no blockItem, create a default one based on item name
            if (!blockItemValue) {
              // Extract item code from item name or use index
              const itemCode = (index + 1).toString().padStart(2, '0');
              blockItemValue = `Block-D, Item-${itemCode}`;
            }

            if (matchingItem) {
              // Use the matching item's data for proper dropdown selection
              newFormData.items[index] = matchingItem.item_name || '';
              newFormData.status[index] = blockItemValue;
            } else {
              // Fallback to API data if no match found
              newFormData.items[index] = item.itemName || '';
              newFormData.status[index] = blockItemValue;
            }

            newFormData.currentYear[index] = item.currentYearValue || '';
            newFormData.previousYear[index] = item.previousYearValue || '';

            console.log(`Final status for row ${index}:`, newFormData.status[index]);
          }
        });

        // Map API response to Remarks List from remarks array
        let mappedRemarksList = [];
        if (response.data.remarks && Array.isArray(response.data.remarks)) {
          mappedRemarksList = response.data.remarks.map((remark, index) => {
            // Extract block letter from "BLOCK C" format (e.g., "BLOCK C" -> "C")
            let blockLetter = '';
            if (remark.block) {
              // Remove "BLOCK " prefix and get the letter
              blockLetter = remark.block.replace(/^BLOCK\s+/i, '').trim();
            }

            return {
              slNo: index + 1,
              blocks: blockLetter || remark.block || '',
              columns: remark.columnsName || '',
              items: remark.items || remark.Items || '',
              remarks: remark.remarks || '',
              remarksCreatedOn: remark.createdDate || remark.createdTime || '',
              remarksBy: remark.updatedBy || '',
              role: remark.updatedBy || ''
            };
          });
          setRemarksList(mappedRemarksList);
        } else {
          // Fallback: if remarks array is not available, use empty array
          setRemarksList([]);
        }

        // Set default in Items dropdown based on remarksList availability
        // If remarksList is empty, set "SELECT_REMARKS", otherwise set "ALL"
        setRemarksData({
          remarksBlock: (mappedRemarksList.length > 0) ? 'ALL' : 'SELECT_REMARKS',
          remarksItems: '',
          remarks: ''
        });

        setFormData(newFormData);

        // Set visible rows based on data
        const filledRows = existingData.length;
        setVisibleRows(Math.max(1, Math.min(MAX_ROWS, filledRows + 1)));

        dataLoadedRef.current = true;
      }
    } catch (error) {
      console.error('Error loading existing BlockD data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Please check your permissions.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        console.log('No existing BlockD data found');
        toast.info('No existing data found for this DSL and year', messages.COLOR_RED);
      } else {
        toast.error('Error loading data. Please try again.', messages.COLOR_RED);
      }
    } finally {
      isLoadingRef.current = false;
      // Hide global loading
      showLoading(false);
    }
  };

  // Save data to API
  const handleSaveToAPI = async () => {
    if (!headerData.despatchSlNo || headerData.despatchSlNo === 'DSL No. not set') {
      toast.error('Please ensure DSL No. is available', messages.COLOR_RED);
      return false;
    }

    // Check if token exists before making API call
    let token = localStorage.getItem('jwt'); // Use JWT token first

    // Try alternative token keys if 'jwt' is not found
    if (!token) {
      token = localStorage.getItem('token') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('accessToken') ||
        localStorage.getItem('userToken');
    }

    if (!token) {
      toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
      return false;
    }

    // Show global loading
    showLoading(true);

    try {
      const dataToSave = [];

      for (let i = 0; i < visibleRows; i++) {
        const item = {
          item_name: formData.items[i] || '',
          current_year_value: formData.currentYear[i] || '',
          previous_year_value: formData.previousYear[i] || '',
          block_item: formData.status[i] || ''
        };

        // Only add if at least one field has data
        if (item.item_name || item.current_year_value || item.previous_year_value) {

          // Extract item_code from block_item (e.g., "Block-D, Item-7" -> "7")
          let itemCode = 0;
          if (item.block_item && item.block_item.includes('Item-')) {
            const match = item.block_item.match(/Item-(\d+)/);
            itemCode = match ? parseInt(match[1]) : 0;
          }

          // Fallback: Find item_code from blockItemData if not found in block_item
          if (itemCode === 0) {
            const matchingItem = blockItemData.find(dropdownItem =>
              dropdownItem.item_name === item.item_name
            );
            itemCode = matchingItem ? (matchingItem.item_code ? parseInt(matchingItem.item_code) : 0) : 0;
          }

          const payload = {
            dsl_no: headerData.despatchSlNo,
            survey_year: headerData.surveyYear.split('-')[0] || '2022', // Extract year from format like "2022-23"
            code: itemCode,
            item_name: item.item_name,
            block_item: item.block_item,
            BLOCK_ID: 'D', // Block D for Balance Sheet Data
            current_year_value: item.current_year_value ? parseFloat(item.current_year_value) : 0,
            previous_year_value: item.previous_year_value ? parseFloat(item.previous_year_value) : 0
          };

          dataToSave.push(payload);
        }
      }

      if (dataToSave.length === 0) {
        toast.warning('No data to save', messages.COLOR_RED);
        return false;
      }

      // Build remarks array if remarks data is available
      const remarksArray = [];
      if (remarksData.remarks && remarksData.remarks.trim() !== '') {
        const remarkPayload = {
          block: 'Block D',
          dslNo: headerData.despatchSlNo,
          surveyYear: parseInt(headerData.surveyYear.split('-')[0]) || 2024,
          columnsName: remarksData.remarksItems || '',
          items: remarksData.remarksBlock === 'ALL' || remarksData.remarksBlock === 'SELECT_REMARKS' ? '' : remarksData.remarksBlock,
          remarks: remarksData.remarks || ''
        };
        remarksArray.push(remarkPayload);
      }

      // Build final payload with data and remarks
      const finalPayload = {
        data: dataToSave,
        remarks: remarksArray
      };

      console.log('Sending BlockD data to API:', finalPayload);
      console.log('API URL:', `${API_URL}api/blockd/insert-item`);

      // Get fresh token and create auth config
      let freshToken = localStorage.getItem('jwt'); // Use JWT token first
      if (!freshToken) {
        freshToken = localStorage.getItem('token') ||
          localStorage.getItem('authToken') ||
          localStorage.getItem('accessToken') ||
          localStorage.getItem('userToken');
      }

      if (!freshToken) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        return false;
      }

      const cleanToken = freshToken.replace(/['"]/g, '').trim();

      const authConfig = {
        headers: {
          'Authorization': `Bearer ${cleanToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      };

      const response = await axios.post(`${API_URL}api/blockd/insert-item`, finalPayload, authConfig);

      console.log('BlockD API Response:', response.data);

      if (response.data && response.data.status) {
        toast.success('Balance Sheet Data saved successfully!', messages.COLOR_RED);

        // Mark page as saved for green header
        markPageAsSaved(PAGE_NAMES.BALANCE_SHEET_DATA);
        // Refresh balance sheet validation to update header colors
        if (headerData.despatchSlNo && headerData.surveyYear) {
          await refreshBalanceSheetValidation(headerData.despatchSlNo, headerData.surveyYear);
        }

        return true;
      } else {
        toast.error('Failed to save Balance Sheet Data', messages.COLOR_RED);
        return false;
      }
    } catch (error) {
      console.error('Error saving BlockD data:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Please check your permissions or contact administrator.', messages.COLOR_RED);
        console.error('403 Error Details:', error.response?.data);
      } else if (error.response?.status === 400) {
        toast.error('Invalid data. Please check your inputs.', messages.COLOR_RED);
        console.error('400 Error Details:', error.response?.data);
      } else {
        toast.error(`Error saving data: ${error.message}`, messages.COLOR_RED);
      }
      return false;
    } finally {
      // Hide global loading
      showLoading(false);
    }
  };

  // Delete single row (Action button) - use same Custom.js helper as InputItemsIndigenous
  const handleDeleteRow = (index) => {
    if (index >= visibleRows) return;

    // Adapter: convert this page's parallel arrays into an object table structure
    // expected by `handleDeleteTableRow`, so delete shifts rows up.
    const itemData = {};
    for (let i = 0; i < visibleRows; i++) {
      itemData[i] = {
        id: i,
        itemDesc: formData.items[i] || '',
        currentYear: formData.currentYear[i] || '',
        previousYear: formData.previousYear[i] || '',
        status: formData.status[i] || ''
      };
    }

    const didDelete = handleDeleteTableRow(
      index,
      itemData,
      (reindexedData) => {
        const nextItems = Array(MAX_ROWS).fill('');
        const nextCurrentYear = Array(MAX_ROWS).fill('');
        const nextPreviousYear = Array(MAX_ROWS).fill('');
        const nextStatus = Array(MAX_ROWS).fill('');

        const len = Math.min(MAX_ROWS, Object.keys(reindexedData || {}).length);
        for (let i = 0; i < len; i++) {
          const row = reindexedData[i] || {};
          nextItems[i] = row.itemDesc || '';
          nextCurrentYear[i] = row.currentYear || '';
          nextPreviousYear[i] = row.previousYear || '';
          nextStatus[i] = row.status || '';
        }

        setFormData(prev => ({
          ...prev,
          items: nextItems,
          currentYear: nextCurrentYear,
          previousYear: nextPreviousYear,
          status: nextStatus
        }));
      },
      MAX_ROWS,
      true
    );

    if (didDelete) {
      setVisibleRows(prev => Math.max(1, prev - 1));
    }
  };

  // Generate table rows dynamically
  const generateTableRows = () => {
    const rows = [];
    // Use hook data
    const itemsToUse = blockItemData.length > 0 ? blockItemData : allItemsList;
    const currentFilteredItems = filteredBlockItems.length > 0 ? filteredBlockItems : itemsToUse;

    // Get already selected items to filter them out
    const selectedItems = formData.items.filter(item => item && item.trim() !== '');

    for (let i = 0; i < visibleRows; i++) {

      rows.push(
        <tr key={i}>
          <td style={{ textAlign: 'center', fontWeight: '500' }}>{i + 1}</td>
          <td>
            <div className="balance-sheet-item-dropdown position-relative" style={{ position: 'relative', overflow: 'visible' }}>
              <input
                type="text"
                className="form-control"
                value={formData.items[i] || ''}
                placeholder={isLoadingBlockItems ? 'Loading...' : 'Select Item'}
                data-row={i}
                style={{
                  textAlign: 'center',
                  paddingRight: '30px',
                  fontSize: '12px',
                  minHeight: '32px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
                onChange={(e) => {
                  handleSearchChange(i, e.target.value);
                  // Update form data for manual input
                  const newItems = [...formData.items];
                  const newStatus = [...formData.status];
                  newItems[i] = e.target.value;

                  // Clear status if item is cleared
                  if (e.target.value.trim() === '') {
                    newStatus[i] = '';
                  }

                  setFormData({ ...formData, items: newItems, status: newStatus });
                  // Reset selected index when typing
                  setSelectedIndex(prev => ({ ...prev, [i]: -1 }));
                }}
                onFocus={(e) => {
                  // Open dropdown on focus if it's closed (keyboard + some touch devices)
                  if (!dropdownStates[i]) {
                    openDropdown(i);
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Only toggle if dropdown wasn't just opened by mousedown/touch
                  if (!justOpenedRef.current[i]) {
                    toggleDropdown(i);
                  } else {
                    justOpenedRef.current[i] = false;
                  }
                }}
                onMouseDown={(e) => {
                  // Open dropdown on mousedown (desktop)
                  if (!dropdownStates[i]) {
                    openDropdown(i);
                  }
                }}
                onTouchStart={(e) => {
                  // Open dropdown on touch (mobile/tablet) - touch fires before focus on many devices
                  if (!dropdownStates[i]) {
                    openDropdown(i);
                  }
                }}
                onKeyDown={(e) => {
                  const filteredList = currentFilteredItems
                    .filter(singleItem => {
                      const isAlreadySelected = selectedItems.some((selectedItem, selectedIndex) =>
                        selectedIndex !== i && selectedItem === (singleItem.item_name || singleItem.itemCodeName)
                      );
                      return !isAlreadySelected;
                    });
                  handleKeyDown(e, i, filteredList);
                }}
                disabled={isLoadingBlockItems}
              />
              <i className="fa fa-chevron-down position-absolute"
                style={{
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#666',
                  pointerEvents: 'none'
                }}></i>
              {dropdownStates[i] && (
                <div
                  className="position-absolute bg-white border rounded shadow-sm custom-dropdown"
                  style={getDropdownStyle(i)}
                >
                  {currentFilteredItems.length > 0 ? (
                    <>
                      {currentFilteredItems
                        .filter(singleItem => {
                          // Filter out already selected items (except current row)
                          const isAlreadySelected = selectedItems.some((selectedItem, selectedIndex) =>
                            selectedIndex !== i && selectedItem === (singleItem.item_name || singleItem.itemCodeName)
                          );
                          return !isAlreadySelected;
                        })
                        .map((singleItem, index) => {
                          const isSelected = selectedIndex[i] === index;
                          return (
                            <div
                              key={index}
                              ref={(el) => {
                                if (el) {
                                  dropdownItemRefs.current[`${i}-${index}`] = el;
                                }
                              }}
                              role="option"
                              aria-selected={isSelected}
                              className="px-3 py-2 cursor-pointer"
                              style={{
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0',
                                fontSize: '12px',
                                wordWrap: 'break-word',
                                whiteSpace: 'normal',
                                minHeight: '40px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                outline: isSelected ? '2px solid #1976d2' : 'none',
                                outlineOffset: '-2px'
                              }}
                              onClick={() => handleItemChange(i, singleItem)}
                              onTouchEnd={(e) => {
                                // Touch devices: select immediately (some devices don't fire click on divs)
                                e.preventDefault();
                                handleItemChange(i, singleItem);
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f8f9fa';
                                setSelectedIndex(prev => ({ ...prev, [i]: index }));
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.target.style.backgroundColor = 'white';
                                }
                              }}
                              tabIndex={-1}
                            >
                              <div style={{
                                fontWeight: 'bold',
                                marginBottom: '2px',
                                wordBreak: 'break-word',
                                lineHeight: '1.3'
                              }}>
                                {singleItem.item_name || singleItem.itemCodeName}
                              </div>
                              {singleItem.block_item && (
                                <div style={{
                                  color: '#666',
                                  fontSize: '11px',
                                  wordBreak: 'break-word',
                                  lineHeight: '1.2'
                                }}>
                                  {singleItem.block_item}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      {hasMoreBlockItems && (
                        <div
                          className="px-3 py-2 text-center text-muted"
                          onClick={loadMoreBlockItemData}
                          style={{ cursor: 'pointer' }}
                        >
                          {isLoadingMoreBlockItems ? 'Loading more...' : 'Load more items'}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="px-3 py-2 text-muted" style={{ fontSize: '12px' }}>
                      {isLoadingBlockItems ? 'Loading items...' : 'No items found'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </td>
          <td><input
            type="text"
            className="form-control"
            value={formData.currentYear[i]}
            onChange={(e) => handleInputChange(i, 'currentYear', e.target.value)}
            placeholder="Enter Current Year Value"
            maxLength="15"
            pattern="[0-9]*\.?[0-9]*"
            disabled={!formData.items[i] || formData.items[i].trim() === ''}
            style={{
              fontSize: '12px',
              minHeight: '32px',
              textAlign: 'right',
              backgroundColor: (!formData.items[i] || formData.items[i].trim() === '') ? '#f5f5f5' : '#fff',
              cursor: (!formData.items[i] || formData.items[i].trim() === '') ? 'not-allowed' : 'text'
            }}
          /></td>
          <td><input
            type="text"
            className="form-control"
            value={formData.previousYear[i]}
            onChange={(e) => handleInputChange(i, 'previousYear', e.target.value)}
            placeholder="Enter Previous Year Value"
            maxLength="15"
            pattern="[0-9]*\.?[0-9]*"
            disabled={!formData.items[i] || formData.items[i].trim() === ''}
            style={{
              fontSize: '12px',
              minHeight: '32px',
              textAlign: 'right',
              backgroundColor: (!formData.items[i] || formData.items[i].trim() === '') ? '#f5f5f5' : '#fff',
              cursor: (!formData.items[i] || formData.items[i].trim() === '') ? 'not-allowed' : 'text'
            }}
          /></td>
          <td><input type="text" className="form-control" value={formData.status[i] || ''} disabled style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', fontSize: '11px', minHeight: '32px', textAlign: 'center', fontWeight: '500', color: '#495057' }} /></td>
          <td>
            {i !== 0 && i !== (visibleRows - 1) && (
              <a
                href="#"
                className="d-block text-center"
                onClick={(e) => {
                  e.preventDefault();
                  handleDeleteRow(i);
                }}
                title={`Delete Row ${i + 1}`}
              >
                <i className="fa fa-trash"></i>
              </a>
            )}
          </td>
        </tr>
      );
    }
    return rows;
  };

  // Filter remarks list using centralized function from Custom.js
  const filteredRemarksList = useMemo(() => {
    return filterRemarksList(remarksList, remarksData);
  }, [remarksList, remarksData]);

  return (
    <>
      <div className="container-fluid py-3">
        {/* Header Section - Using HeaderInfo Component with Table Styles */}
        <HeaderInfo
          title="Balance Sheet Data"
          icon="fa fa-file-text"
          iconColor="#28a745"
          titleColor="#1976d2"
          backgroundColor=""
          dispatchSlNo={headerData.despatchSlNo}
          permanentSlNo={headerData.permanentSlNo}
          companyName={headerData.companyName}
          companyPlaceholder="Enter Company Name"
          showBlockCType={false} // Balance Sheet doesn't need Block C Type
          includeTableStyles={true} // Table styles needed for this page
        />

        {/* Main Content Area */}
        <div className="card mb-3" style={{ position: 'relative', zIndex: 1 }}>
          <div className="card-body" style={{ overflow: 'visible', position: 'relative', paddingBottom: '50px' }}>

            <div className="row">
              <div className="col-12">
                <div className="table-responsive" style={{ overflow: 'visible', position: 'relative', zIndex: 1 }}>
                  <div className="d-flex">
                    {/* Main Data Table */}
                    <div className="flex-grow-1" style={{ position: 'relative', zIndex: 1 }}>
                      <table className="table table-bordered mb-0" style={{ position: 'relative', zIndex: 1, marginBottom: '0' }}>
                        <thead>
                          <tr>
                            <th style={{ width: '5%' }}>SL No.</th>
                            <th style={{ width: '30%' }}>ITEMS</th>
                            <th style={{ width: '18%' }}>Current Year</th>
                            <th style={{ width: '18%' }}>Previous Year</th>
                            <th style={{ width: '19%' }}>Status</th>
                            <th style={{ width: '10%' }}>Action</th>
                          </tr>
                          <tr>
                            <th style={{ width: '5%' }}>(1)</th>
                            <th style={{ width: '30%' }}>(2)</th>
                            <th style={{ width: '18%' }}>(3)</th>
                            <th style={{ width: '18%' }}>(4)</th>
                            <th style={{ width: '19%' }}>(5)</th>
                            <th style={{ width: '10%' }}>(6)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {generateTableRows()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='card mb-3'>
          <div className='card-body'>
            <div className='row'>
              <div className='col-md-12'>
                <h5 className='mb-3 border-bottom pb-2' style={{ fontWeight: 'bold' }}>As Per Balance Sheet:</h5>
              </div>
              <div className='col-md-6'>
                <div className='card'>
                  <div className='card-header'>
                    <h6 className='mb-0 pt-2 pb-2' style={{ fontWeight: 'bold' }}>Total Assets:</h6>
                  </div>
                  <div className='card-body'>
                    <div className='row'>
                      <div className='col-md-6'>
                        <label className='mb-0 me-2'>Opening:</label>
                        <span className='text-muted'>0</span>
                      </div>
                      <div className='col-md-6'>
                        <label className='mb-0 me-2'>Closing:</label>
                        <span className='text-muted'>0</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-md-6'>
                <div className='card'>
                  <div className='card-header'>
                    <h6 className='mb-0 pt-2 pb-2' style={{ fontWeight: 'bold' }}>Total Liabilities:</h6>
                  </div>
                  <div className='card-body'>
                    <div className='row'>
                      <div className='col-md-6'>
                        <label className='mb-0 me-2'>Opening:</label>
                        <span className='text-muted'>0</span>
                      </div>
                      <div className='col-md-6'>
                        <label className='mb-0 me-2'>Closing:</label>
                        <span className='text-muted'>0</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='row'>
          <div className='col-md-6 remarks-section'>
            <Remarks
              remarksData={remarksData}
              setRemarksData={setRemarksData}
              blockItemData={blockItemData}
              filteredBlockItems={filteredBlockItems}
              isLoadingBlockItems={isLoadingBlockItems}
              debouncedBlockItemSearch={debouncedBlockItemSearch}
              loadMoreBlockItemData={loadMoreBlockItemData}
              hasMoreBlockItems={hasMoreBlockItems}
              isLoadingMoreBlockItems={isLoadingMoreBlockItems}
              selectedItemsFromTable={formData.items.filter(item => item && item.trim() !== '')}
              remarksList={remarksList}
              availableColumns={[3, 4]}
            />
          </div>

          <div className='col-md-6'>
            <div className='card'>
              <div className='card-body'>
                <h6 className='mb-3' style={{ fontWeight: 'bold', color: '#f56565' }}><i className='fa fa-exclamation-triangle me-2'></i>Note</h6>
                <ul className='list-styled' style={{ fontSize: '14px', color: '#f56565' }}>
                  <li>Col-4 and 5 are Mandatory</li>
                  <li>For OTHERS item, Col-3 and Blocks are also Mandatory</li>

                </ul>
              </div>
            </div>
          </div>


        </div>

        {/* Action Buttons */}
        <div className="row">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print
              <i className="fa fa-print ms-1"></i>
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={handleSaveToAPI}
            >
              Save
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={handleReset}
            >
              Reset
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/asiScheduleMainMenu')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/fixedAssets')}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={async () => {
                const saved = await handleSaveToAPI();
                if (saved) {
                  navigate('/profitAndLossAccount');
                }
              }}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>

          </div>
        </div>

        {/* Remarks Lists Table */}
        <Remarks__list remarksData={filteredRemarksList} />
      </div>
    </>
  );
}

export default BalanceSheetData; 